import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronUp, ChevronDown, Maximize2, Minimize2 } from 'lucide-react';
import {
  buildHighlightedLines,
  caretPosition,
  findMatches,
  MAX_HIGHLIGHT_LENGTH
} from '../utils/codeEditorHighlight';
import './codeEditorField.css';

/**
 * A code box that behaves like an editor.
 *
 * The Custom HTML/CSS block is written somewhere else — by hand, or by an
 * assistant working from the downloadable brief — and then pasted in here to be
 * corrected. Two plain textareas made that last part miserable: no way to find
 * the one class you wanted to change, and no line numbers to hold on to while
 * reading a hundred lines of someone else's markup.
 *
 * The structure is the standard one for this: a textarea whose text is
 * transparent, laid exactly over a backdrop that draws the same characters with
 * syntax colours and search highlights. The textarea keeps the caret, the
 * selection, the undo stack and every native editing behaviour; the backdrop
 * only paints. They must agree on font, padding and wrapping to the pixel,
 * which is why both take their metrics from the same CSS custom properties.
 *
 * Line numbers live in a third element pinned over the left edge. Their heights
 * are measured from the backdrop's own line boxes when soft wrap is on, so a
 * line that wraps to three rows gets one number three rows tall rather than
 * three numbers.
 *
 * Full screen is a portal rather than a `position: fixed` box, because the
 * dialog this sits in clips its own overflow: a fixed box laid out inside it is
 * still clipped by it, so the only way out of the modal is out of its subtree.
 */

const INDENT = '  ';
const WRAP_PREF_KEY = 'lp.codeEditor.softWrap';
// A zero-width space: the character that gives an empty line a line box to be
// measured against without occupying a column.
const BLANK_LINE = '\u200b';

// Tags that never have a closing tag, so Enter after one should not indent.
const VOID_TAGS = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr'
]);

const readWrapPref = () => {
  try {
    return window.localStorage.getItem(WRAP_PREF_KEY) !== 'off';
  } catch (err) {
    return true;
  }
};

const writeWrapPref = (on) => {
  try {
    window.localStorage.setItem(WRAP_PREF_KEY, on ? 'on' : 'off');
  } catch (err) {
    /* a private window is not a reason to fail */
  }
};

// Does this text end with an opening tag that something will be nested inside?
const opensTag = (text) => {
  const match = text.match(/<([a-zA-Z][\w:.-]*)((?:"[^"]*"|'[^']*'|[^"'>])*)>$/);
  if (!match) return false;
  if (match[2].trimEnd().endsWith('/')) return false;
  return !VOID_TAGS.has(match[1].toLowerCase());
};

const sameNumbers = (a, b) => a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * @param {object} [props.jumpAction] - an optional extra tool in the header
 *   row, leading it, ahead of Wrap. It is not a text-editing control: the two
 *   Custom HTML/CSS boxes use it to move the dialog between each other, which
 *   otherwise means scrolling past three hundred lines of markup in one
 *   direction or the other. Shaped
 *   `{ label, title, onActivate, direction }` — `label` becomes the accessible
 *   name, because a lone arrow icon says nothing about where it goes, and
 *   `direction` ('down', the default, or 'up') picks the arrow. Kept first
 *   rather than mixed in among the editing tools: where it takes you is the
 *   question, and the arrow answers it at the start of the row instead of
 *   between two toggles.
 */
const CodeEditorField = ({
  id,
  label,
  value,
  onChange,
  language = 'html',
  placeholder = '',
  hint = null,
  height = 320,
  accent = '#a21caf',
  jumpAction = null
}) => {
  const text = typeof value === 'string' ? value : '';

  const rootRef = useRef(null);
  const frameRef = useRef(null);
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);
  const linesRef = useRef(null);
  const gutterRef = useRef(null);
  const rulerRef = useRef(null);
  const findInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Where the caret was when full screen was toggled. Moving the editor into
  // (and out of) the portal builds new DOM nodes, so the caret, the selection
  // and the scroll have to be carried across by hand.
  const restoreRef = useRef(null);

  const [findOpen, setFindOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [replacement, setReplacement] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [wrap, setWrap] = useState(readWrapPref);
  const [caret, setCaret] = useState({ line: 1, column: 1 });
  const [rowHeights, setRowHeights] = useState([]);
  const [widthTick, setWidthTick] = useState(0);
  const [expanded, setExpanded] = useState(false);
  // The height the box had in the dialog, held open while it is away so the
  // page behind does not reshuffle under the overlay and land somewhere else
  // when it comes back.
  const [placeholderHeight, setPlaceholderHeight] = useState(0);

  const matches = useMemo(
    () => findMatches(text, findOpen ? query : '', { caseSensitive, wholeWord }),
    [text, query, findOpen, caseSensitive, wholeWord]
  );

  const lines = useMemo(
    () => buildHighlightedLines(text, {
      language,
      matches,
      activeIndex: matches.length ? activeIndex : -1,
      syntax: true
    }),
    [text, language, matches, activeIndex]
  );

  const lineCount = lines.length;
  const digits = Math.max(2, String(lineCount).length);

  useEffect(() => {
    setActiveIndex((index) => {
      if (!matches.length) return 0;
      return Math.min(index, matches.length - 1);
    });
  }, [matches]);

  /* ---- keeping the three layers lined up ------------------------------- */

  const syncScroll = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const x = textarea.scrollLeft;
    const y = textarea.scrollTop;
    if (linesRef.current) {
      linesRef.current.style.transform = `translate(${-x}px, ${-y}px)`;
    }
    if (gutterRef.current) {
      gutterRef.current.style.transform = `translateY(${-y}px)`;
    }
  }, []);

  // The backdrop has no scrollbar of its own, so it would be a scrollbar's
  // width wider than the textarea's content box and would wrap in different
  // places. Pin it to the textarea's measured width instead.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const backdrop = backdropRef.current;
    if (!textarea || !backdrop) return;
    backdrop.style.width = `${textarea.clientWidth}px`;
    // `expanded` is in here because full screen builds new elements at a new
    // width, and the inline width does not travel with them.
  }, [wrap, widthTick, text, expanded]);

  // One number per line, each as tall as the line it labels. With wrap off a
  // line is always exactly one row, so the measuring is skipped entirely.
  useLayoutEffect(() => {
    if (!wrap) {
      setRowHeights((prev) => (prev.length ? [] : prev));
      return;
    }
    const container = linesRef.current;
    if (!container) return;
    const next = Array.from(container.children, (el) => el.offsetHeight);
    setRowHeights((prev) => (sameNumbers(prev, next) ? prev : next));
  }, [lines, wrap, widthTick, expanded]);

  // Anything that changes the frame's width changes where the text wraps, and
  // the backdrop has to be re-measured against it. The observer catches the
  // author dragging the resize handle; the window listener is there for the
  // browsers that do not have one, and for the moments a hidden tab does not
  // deliver its callbacks.
  useEffect(() => {
    const remeasure = () => setWidthTick((tick) => tick + 1);
    const frame = frameRef.current;
    const observer = frame && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(remeasure)
      : null;
    if (observer) observer.observe(frame);
    window.addEventListener('resize', remeasure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', remeasure);
    };
    // Going full screen builds a new frame element, so the old one is no
    // longer the thing to watch.
  }, [expanded]);

  useEffect(() => {
    syncScroll();
  }, [syncScroll, text, wrap]);

  /* ---- full screen ------------------------------------------------------- */

  const toggleExpanded = useCallback(() => {
    const textarea = textareaRef.current;
    restoreRef.current = textarea
      ? {
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
        top: textarea.scrollTop,
        left: textarea.scrollLeft,
        focused: document.activeElement === textarea
      }
      : null;
    setExpanded((on) => {
      if (!on && rootRef.current) setPlaceholderHeight(rootRef.current.offsetHeight);
      return !on;
    });
  }, []);

  // Put the caret back where it was before the move, in the new textarea.
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    const restore = restoreRef.current;
    restoreRef.current = null;
    if (!textarea || !restore) return;
    textarea.setSelectionRange(restore.start, restore.end);
    textarea.scrollTop = restore.top;
    textarea.scrollLeft = restore.left;
    if (restore.focused) textarea.focus();
    syncScroll();
  }, [expanded, syncScroll]);

  const syncCaret = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    setCaret(caretPosition(textarea.value, textarea.selectionStart));
  }, []);

  /* ---- editing ---------------------------------------------------------- */

  // Edit through the browser wherever it will let us, so Ctrl+Z still undoes
  // an indent or a replace the way it undoes typing. The fallback is a plain
  // controlled update, which is what the tests and older engines take.
  const replaceRange = useCallback((from, to, insert, selection) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(from, to);

    let handled = false;
    try {
      if (typeof document.execCommand === 'function') {
        handled = insert === ''
          ? document.execCommand('delete')
          : document.execCommand('insertText', false, insert);
      }
    } catch (err) {
      handled = false;
    }

    if (!handled) {
      const next = textarea.value.slice(0, from) + insert + textarea.value.slice(to);
      onChangeRef.current(next);
    }

    const caretAt = from + insert.length;
    const range = selection || [caretAt, caretAt];
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.setSelectionRange(range[0], range[1]);
      syncCaret();
    });
  }, [syncCaret]);

  const indentSelection = useCallback((outdent) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const source = textarea.value;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const spansLines = source.slice(start, end).includes('\n');

    if (!outdent && !spansLines) {
      replaceRange(start, end, INDENT);
      return;
    }

    const blockStart = source.lastIndexOf('\n', start - 1) + 1;
    let blockEnd = source.indexOf('\n', end);
    if (blockEnd === -1) blockEnd = source.length;

    let firstDelta = 0;
    let totalDelta = 0;
    const rewritten = source.slice(blockStart, blockEnd).split('\n').map((line, index) => {
      if (outdent) {
        const lead = line.match(/^(\t| {1,2})/);
        const removed = lead ? lead[0].length : 0;
        if (index === 0) firstDelta = -removed;
        totalDelta -= removed;
        return line.slice(removed);
      }
      if (index === 0) firstDelta = INDENT.length;
      totalDelta += INDENT.length;
      return INDENT + line;
    }).join('\n');

    replaceRange(blockStart, blockEnd, rewritten, [
      Math.max(blockStart, start + firstDelta),
      Math.max(blockStart, end + totalDelta)
    ]);
  }, [replaceRange]);

  const autoIndentNewline = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return false;
    const source = textarea.value;
    const start = textarea.selectionStart;
    if (start !== textarea.selectionEnd) return false;

    const lineStart = source.lastIndexOf('\n', start - 1) + 1;
    const before = source.slice(lineStart, start);
    const lead = before.match(/^[ \t]*/);
    let indent = lead ? lead[0] : '';
    const trimmed = before.trimEnd();
    if (trimmed.endsWith('{') || opensTag(trimmed)) indent += INDENT;
    if (!indent) return false;

    replaceRange(start, start, `\n${indent}`);
    return true;
  }, [replaceRange]);

  /* ---- find and replace -------------------------------------------------- */

  const scrollMatchIntoView = useCallback((match) => {
    const textarea = textareaRef.current;
    const container = linesRef.current;
    if (!textarea || !container || !match) return;

    const before = textarea.value.slice(0, match.start);
    const lineIndex = before.split('\n').length - 1;
    const lineEl = container.children[lineIndex];
    if (lineEl) {
      const target = lineEl.offsetTop - (textarea.clientHeight - lineEl.offsetHeight) / 2;
      textarea.scrollTop = Math.max(0, target);
    }

    if (!wrap && rulerRef.current) {
      const charWidth = rulerRef.current.getBoundingClientRect().width / 10;
      const column = match.start - (before.lastIndexOf('\n') + 1);
      const x = column * charWidth;
      const view = textarea.clientWidth;
      if (x < textarea.scrollLeft || x > textarea.scrollLeft + view - charWidth * 4) {
        textarea.scrollLeft = Math.max(0, x - view * 0.4);
      }
    }
    syncScroll();
  }, [syncScroll, wrap]);

  const goToMatch = useCallback((index) => {
    if (!matches.length) return;
    const wrapped = ((index % matches.length) + matches.length) % matches.length;
    const match = matches[wrapped];
    setActiveIndex(wrapped);
    const textarea = textareaRef.current;
    if (textarea) {
      // Select it without stealing focus: the find box stays typeable, and the
      // selection is already in place if the author tabs into the code to edit.
      textarea.setSelectionRange(match.start, match.end);
      setCaret(caretPosition(textarea.value, match.start));
    }
    scrollMatchIntoView(match);
  }, [matches, scrollMatchIntoView]);

  const step = useCallback((delta) => {
    if (!matches.length) return;
    goToMatch(activeIndex + delta);
  }, [activeIndex, goToMatch, matches.length]);

  const openFind = useCallback((withReplace) => {
    const textarea = textareaRef.current;
    const selected = textarea
      ? textarea.value.slice(textarea.selectionStart, textarea.selectionEnd)
      : '';
    if (selected && !selected.includes('\n')) setQuery(selected);
    setFindOpen(true);
    if (withReplace) setReplaceOpen(true);
    requestAnimationFrame(() => {
      if (findInputRef.current) findInputRef.current.select();
    });
  }, []);

  const closeFind = useCallback(() => {
    setFindOpen(false);
    setReplaceOpen(false);
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  const replaceCurrent = useCallback(() => {
    const match = matches[activeIndex];
    if (!match) return;
    replaceRange(match.start, match.end, replacement);
    requestAnimationFrame(() => {
      if (findInputRef.current) findInputRef.current.focus();
    });
  }, [activeIndex, matches, replaceRange, replacement]);

  const replaceAll = useCallback(() => {
    if (!matches.length) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    const source = textarea.value;
    let rebuilt = '';
    let cursor = 0;
    matches.forEach((match) => {
      rebuilt += source.slice(cursor, match.start) + replacement;
      cursor = match.end;
    });
    rebuilt += source.slice(cursor);
    // One replacement of the whole document, so it is one undo step.
    replaceRange(0, source.length, rebuilt, [0, 0]);
    setActiveIndex(0);
    requestAnimationFrame(() => {
      if (findInputRef.current) findInputRef.current.focus();
    });
  }, [matches, replaceRange, replacement]);

  useEffect(() => {
    if (!findOpen || !matches.length) return;
    // A new query lands on the first match at or after the caret.
    const textarea = textareaRef.current;
    const from = textarea ? textarea.selectionStart : 0;
    const index = matches.findIndex((match) => match.start >= from);
    const target = index === -1 ? 0 : index;
    setActiveIndex(target);
    scrollMatchIntoView(matches[target]);
    // Only when the query itself changes — stepping through is handled by
    // goToMatch, which must not be undone by this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, caseSensitive, wholeWord, findOpen]);

  /* ---- keyboard ---------------------------------------------------------- */

  const handleKeyDown = (event) => {
    const mod = event.ctrlKey || event.metaKey;
    const key = event.key;

    // Every shortcut the editor answers is also stopped here. The app binds
    // keys of its own at the document, and a keystroke that has already done
    // something inside the code box must not do a second thing outside it.
    if (mod && (key === 'f' || key === 'F')) {
      event.preventDefault();
      event.stopPropagation();
      openFind(false);
      return;
    }
    if (mod && (key === 'h' || key === 'H')) {
      event.preventDefault();
      event.stopPropagation();
      openFind(true);
      return;
    }
    if ((mod && (key === 'g' || key === 'G')) || key === 'F3') {
      event.preventDefault();
      event.stopPropagation();
      step(event.shiftKey ? -1 : 1);
      return;
    }
    if (key === 'Escape') {
      // Swallowed on purpose. Tab indents rather than moving focus, so Escape
      // has to be the way out of the box, one layer at a time: the find bar,
      // then full screen, then focus. The modal behind stays put throughout,
      // which is what keeps a stray Escape mid-edit from throwing the slide
      // away.
      event.preventDefault();
      event.stopPropagation();
      if (findOpen) closeFind();
      else if (expanded) toggleExpanded();
      else event.currentTarget.blur();
      return;
    }
    if (key === 'Tab' && !event.altKey && !mod) {
      event.preventDefault();
      event.stopPropagation();
      indentSelection(event.shiftKey);
      return;
    }
    if (key === 'Enter' && !event.shiftKey && !mod) {
      if (autoIndentNewline()) event.preventDefault();
    }
  };

  const handleFindKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      step(event.shiftKey ? -1 : 1);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeFind();
    }
  };

  const toggleWrap = () => {
    setWrap((on) => {
      writeWrapPref(!on);
      return !on;
    });
  };

  const gutterRows = [];
  for (let i = 0; i < lineCount; i += 1) gutterRows.push(i);

  const matchLabel = query
    ? (matches.length ? `${activeIndex + 1} of ${matches.length}` : 'No results')
    : '';

  // Escape from a button inside the overlay, where the textarea's own handler
  // never sees the key.
  const handleOverlayKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    if (findOpen) closeFind();
    else toggleExpanded();
  };

  const editor = (
    <div
      ref={rootRef}
      className={`lp-code-editor${expanded ? ' lp-code-editor--expanded' : ''}`}
      style={{ '--lp-ce-accent': accent, '--lp-ce-height': `${height}px` }}
    >
      <div className="lp-code-editor__head">
        <label className="lp-code-editor__label" htmlFor={id}>{label}</label>
        <div className="lp-code-editor__tools">
          {jumpAction && (
            <button
              type="button"
              onClick={jumpAction.onActivate}
              className="lp-code-editor__toggle lp-code-editor__toggle--jump"
              title={jumpAction.title || jumpAction.label}
              aria-label={jumpAction.label}
            >
              {jumpAction.direction === 'up'
                ? <ChevronUp size={13} aria-hidden="true" />
                : <ChevronDown size={13} aria-hidden="true" />}
            </button>
          )}
          <button
            type="button"
            onClick={toggleWrap}
            className={`lp-code-editor__toggle${wrap ? ' is-on' : ''}`}
            aria-pressed={wrap}
            title="Wrap long lines"
          >
            Wrap
          </button>
          <button
            type="button"
            onClick={() => (findOpen ? closeFind() : openFind(false))}
            className={`lp-code-editor__toggle${findOpen ? ' is-on' : ''}`}
            aria-expanded={findOpen}
            title="Find and replace (Ctrl+F)"
          >
            <Search size={13} aria-hidden="true" />
            Find
          </button>
          <button
            type="button"
            onClick={toggleExpanded}
            className={`lp-code-editor__toggle${expanded ? ' is-on' : ''}`}
            aria-pressed={expanded}
            title={expanded ? 'Leave full screen (Esc)' : 'Edit full screen'}
          >
            {expanded
              ? <Minimize2 size={13} aria-hidden="true" />
              : <Maximize2 size={13} aria-hidden="true" />}
            {expanded ? 'Exit' : 'Expand'}
          </button>
        </div>
      </div>

      {findOpen && (
        <div className="lp-code-editor__find" role="search" aria-label={`Find in ${label}`}>
          <div className="lp-code-editor__find-row">
            <Search size={14} className="lp-code-editor__find-icon" aria-hidden="true" />
            <input
              ref={findInputRef}
              type="text"
              className="lp-code-editor__find-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleFindKeyDown}
              placeholder="Find in code…"
              aria-label="Find"
              spellCheck={false}
            />
            <span
              className={`lp-code-editor__count${query && !matches.length ? ' is-empty' : ''}`}
              aria-live="polite"
            >
              {matchLabel}
            </span>
            <button
              type="button"
              className={`lp-code-editor__flag${caseSensitive ? ' is-on' : ''}`}
              onClick={() => setCaseSensitive((on) => !on)}
              aria-pressed={caseSensitive}
              title="Match case"
            >
              Aa
            </button>
            <button
              type="button"
              className={`lp-code-editor__flag${wholeWord ? ' is-on' : ''}`}
              onClick={() => setWholeWord((on) => !on)}
              aria-pressed={wholeWord}
              title="Whole word"
            >
              ab|
            </button>
            <button
              type="button"
              className="lp-code-editor__step"
              onClick={() => step(-1)}
              disabled={!matches.length}
              title="Previous match (Shift+Enter)"
              aria-label="Previous match"
            >
              <ChevronUp size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="lp-code-editor__step"
              onClick={() => step(1)}
              disabled={!matches.length}
              title="Next match (Enter)"
              aria-label="Next match"
            >
              <ChevronDown size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`lp-code-editor__flag${replaceOpen ? ' is-on' : ''}`}
              onClick={() => setReplaceOpen((on) => !on)}
              aria-pressed={replaceOpen}
              title="Replace (Ctrl+H)"
            >
              ⇄
            </button>
            <button
              type="button"
              className="lp-code-editor__step"
              onClick={closeFind}
              title="Close find (Esc)"
              aria-label="Close find"
            >
              <X size={15} aria-hidden="true" />
            </button>
          </div>

          {replaceOpen && (
            <div className="lp-code-editor__find-row">
              <span className="lp-code-editor__find-icon" aria-hidden="true">⇄</span>
              <input
                type="text"
                className="lp-code-editor__find-input"
                value={replacement}
                onChange={(event) => setReplacement(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    replaceCurrent();
                  } else if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    closeFind();
                  }
                }}
                placeholder="Replace with…"
                aria-label="Replace with"
                spellCheck={false}
              />
              <button
                type="button"
                className="lp-code-editor__action"
                onClick={replaceCurrent}
                disabled={!matches.length}
              >
                Replace
              </button>
              <button
                type="button"
                className="lp-code-editor__action"
                onClick={replaceAll}
                disabled={!matches.length}
              >
                All
              </button>
            </div>
          )}
        </div>
      )}

      <div
        ref={frameRef}
        className={`lp-code-editor__frame${wrap ? ' is-wrapped' : ''}`}
        style={{ '--lp-ce-gutter': `calc(${digits}ch + 1.35rem)` }}
      >
        {/* Ten monospace zeroes, never shown: the only honest way to know how
            wide one character is when scrolling to a match sideways. */}
        <span ref={rulerRef} className="lp-code-editor__ruler" aria-hidden="true">0000000000</span>

        <div ref={backdropRef} className="lp-code-editor__backdrop" aria-hidden="true">
          <div ref={linesRef} className="lp-code-editor__lines">
            {lines.map((segments, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="lp-code-editor__line">
                {segments.length
                  ? segments.map((segment, position) => (
                    <span
                      // eslint-disable-next-line react/no-array-index-key
                      key={position}
                      className={[
                        segment.className,
                        segment.hit === 2 ? 'lp-ce-hit lp-ce-hit--active' : (segment.hit ? 'lp-ce-hit' : '')
                      ].filter(Boolean).join(' ') || undefined}
                    >
                      {segment.text}
                    </span>
                  ))
                  /* A blank line still needs a line box for its number to be
                     measured against, and a zero-width space is the only
                     character that makes one without occupying a column. */
                  : BLANK_LINE}
              </div>
            ))}
          </div>
        </div>

        <textarea
          id={id}
          ref={textareaRef}
          key={wrap ? 'soft' : 'off'}
          className="lp-code-editor__input"
          value={text}
          onChange={(event) => onChange(event.target.value)}
          onScroll={syncScroll}
          onKeyDown={handleKeyDown}
          onKeyUp={syncCaret}
          onClick={syncCaret}
          onSelect={syncCaret}
          onFocus={syncCaret}
          placeholder={placeholder}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          wrap={wrap ? 'soft' : 'off'}
        />

        <div className="lp-code-editor__gutter" aria-hidden="true">
          <div ref={gutterRef} className="lp-code-editor__gutter-inner">
            {gutterRows.map((index) => (
              <div
                key={index}
                className={`lp-code-editor__num${caret.line === index + 1 ? ' is-current' : ''}`}
                style={wrap && rowHeights[index] ? { height: rowHeights[index] } : undefined}
              >
                {index + 1}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lp-code-editor__status">
        <span>Ln {caret.line}, Col {caret.column}</span>
        <span>{lineCount} {lineCount === 1 ? 'line' : 'lines'}</span>
        <span>{text.length.toLocaleString()} characters</span>
        {text.length > MAX_HIGHLIGHT_LENGTH && <span>Colours off — long file</span>}
        <span className="lp-code-editor__keys">
          Ctrl+F find · Tab indents · Esc {expanded ? 'leaves full screen' : 'leaves the box'}
        </span>
      </div>

      {hint && <p className="lp-code-editor__hint">{hint}</p>}
    </div>
  );

  if (!expanded) return editor;

  return (
    <>
      {/* Holds the dialog's scroll still while the box is elsewhere. */}
      <div
        className="lp-code-editor-placeholder"
        style={{ height: placeholderHeight || undefined }}
        aria-hidden="true"
      />
      {createPortal(
        <div
          className="lp-code-editor-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`${label} — full screen`}
          onKeyDown={handleOverlayKeyDown}
        >
          {editor}
        </div>,
        document.body
      )}
    </>
  );
};

export default CodeEditorField;
